import { useCallback, useState } from 'react';

import { ContractFlowClient } from 'v3/api/client/ContractFlowClient';

/** Shown whenever the referral request comes back as anything other than a 200. */
const INVALID_CODE_MESSAGE = 'Invalid referral code.';

/**
 * Referral code application.
 *
 * Two calls with different jobs:
 *
 * - getReferralCode() only VALIDATES and returns the short description the UI shows.
 * - applyReferral() is what BINDS the code to the contract flow, and returns the
 *   recalculated flow (with the discount) to feed the offer summary.
 *
 * While the input lived on StartDeal the code rode along in the startDeal() body.
 * On ReviewOffer there is no such ride — that endpoint only accepts weekly_cycle —
 * so without applyReferral the code would vanish on reload and never reach the
 * contract.
 *
 * Re-applying a different code is supported and is how a code gets changed: the
 * backend's set_referral() reassigns contract_flow.member_referral and recalculates
 * the offer without advancing the step.
 *
 * There is no un-apply endpoint, so this hook deliberately offers no remove():
 * anything it could do would clear the UI while leaving the code bound server-side.
 */
export const useReferralCode = (
  clubId?: string,
  cfUuid?: string,
  /** Code the contract flow already carries from the database. Without this the
   *  field would start out "not applied" and offer Apply on a code already bound
   *  to the contract, while the summary above showed the applied tag — two
   *  opposite states of the same data. */
  appliedCode?: string,
  /** The rate line for a code that is already bound, straight off the contract
   *  flow. Without it the description would stay blank until the member applied a
   *  code in this session, so a reload showed the code with no rate beside it. */
  appliedDescription?: string
) => {
  const [isApplied, setIsApplied] = useState(Boolean(appliedCode?.trim()));
  /** The code currently bound to the flow, so the UI can tell an edited input
   *  apart from what the backend actually holds. */
  const [boundCode, setBoundCode] = useState(appliedCode?.trim() ?? '');
  const [description, setDescription] = useState(appliedDescription ?? '');
  const [error, setError] = useState('');
  const [isChecking, setIsChecking] = useState(false);

  const apply = useCallback(
    async (code: string) => {
      // Codes are uppercase, and the backend looks them up with an exact match, so
      // "fee20" would 404 on a code that exists. The input only uppercases visually
      // via text-transform, which does not change the value being sent.
      const trimmed = code.trim().toUpperCase();
      if (!trimmed) return null;

      // Without a contract flow there is nothing to bind the code to. Fail loudly
      // rather than reporting a code as applied when it never reached the backend.
      if (!cfUuid) {
        setIsApplied(false);
        setError('We could not apply this code. Try again.');
        return null;
      }

      setIsChecking(true);
      setError('');

      const { data } = await ContractFlowClient().getReferralCode(trimmed, clubId);

      // Anything but a 200 means the code is not usable: the endpoint only answers
      // 200 for a code that passes validation, and raises otherwise (404 unknown,
      // 400 expired / already used / wrong club). So the member is told it is
      // invalid rather than shown whatever the failure happened to carry — the
      // previous code surfaced apiError.message, which for a server error was
      // axios's own "Request failed with status code 500".
      if (!data) {
        setIsChecking(false);
        setIsApplied(false);
        setError(INVALID_CODE_MESSAGE);
        return null;
      }

      const applied = await ContractFlowClient().applyReferral(cfUuid, trimmed);

      // Same reasoning: if binding it failed, the code did not take. "Try again"
      // would be wrong, because for a code the backend rejects retrying never helps.
      if (applied.error) {
        setIsChecking(false);
        setIsApplied(false);
        setError(INVALID_CODE_MESSAGE);
        return null;
      }

      setDescription(data.shortDescription);
      setBoundCode(trimmed);
      setIsApplied(true);
      setIsChecking(false);

      // The normalized code goes back out so callers store what was actually sent,
      // rather than whatever casing was typed.
      return {
        code: trimmed,
        description: data.shortDescription,
        contractFlow: applied.data,
      };
    },
    [clubId, cfUuid]
  );

  return {
    isApplied,
    appliedCode: boundCode,
    description,
    error,
    isChecking,
    apply,
  };
};

export default useReferralCode;
